/**
 * ClickableCustomerName — 클릭 시 CustomerDetailSheet 트리거
 *
 * 고객 이름이 노출되는 모든 위치에서 일관된 UX:
 * - 표시: 이름 또는 fallback ("-")
 * - 클릭: customerId 있으면 CustomerDetailSheet 표시 (재사용)
 * - 비고객(customerId null): 클릭 비활성, 단순 텍스트
 *
 * customer fetch는 CustomerDetailSheet 내부의 useQuery에 의존하지 않고,
 * 컴포넌트 진입 시점에 lazy하게 가져옵니다. (sheet 열기 전엔 호출 X)
 *
 * 사용 예:
 *   <ClickableCustomerName customerId={consult.customerId} name={consult.customerName} />
 *   <ClickableCustomerName customerId={appointment.customerId} name={appointment.customerName} />
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import { customerApi } from "@/api/customer";
import { queryKeys } from "@/lib/queryKeys";
import { CustomerDetailSheet } from "@/components/customer/CustomerDetailSheet";

interface ClickableCustomerNameProps {
  /** 고객 ID — 없으면 비고객으로 간주, 클릭 비활성화 */
  customerId?: number | null;
  /** 표시할 이름 — null이면 "-" */
  name: string | null | undefined;
  className?: string;
  /** 아이콘 표시 여부 (기본 false — 이름은 보통 단독 표시) */
  showIcon?: boolean;
  /** 통화의 실제 발신번호(caller_id) — 통화 맥락에서 전달하면 고객 사이드탭에 번호 불일치 "확인 필요" 경고 표시 */
  callerNumber?: string;
}

export default function ClickableCustomerName({
  customerId,
  name,
  className,
  showIcon = false,
  callerNumber,
}: ClickableCustomerNameProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const displayName = name && name.trim() !== "" ? name : "-";
  const isClickable = customerId != null && customerId > 0;

  // sheetOpen 시점에만 fetch (lazy)
  const { data: customer } = useQuery({
    queryKey: queryKeys.customers.detail(customerId ?? 0),
    queryFn: () => customerApi.getCustomer(customerId!),
    enabled: sheetOpen && isClickable,
    staleTime: 30_000,
  });

  const handleClick = (e: React.MouseEvent) => {
    if (!isClickable) return;
    e.stopPropagation();
    setSheetOpen(true);
  };

  if (!isClickable) {
    return (
      <span className={cn("text-foreground", className)}>{displayName}</span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "inline-flex items-center gap-1 hover:underline hover:text-primary transition-colors cursor-pointer",
          className,
        )}
        title="클릭하여 고객 정보 보기"
        aria-label={`${displayName} 고객 정보 보기`}
      >
        {showIcon && <User className="h-3 w-3 shrink-0" />}
        <span>{displayName}</span>
      </button>

      <CustomerDetailSheet
        customer={customer ?? null}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        callerNumber={callerNumber}
      />
    </>
  );
}
